import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, storeActions } from '../../store'
import type { Configuration } from '../../store/models/types'

import { RetorikLanguageData, RetorikMainComponentConfiguration, RetorikMainComponentProps, RetorikOtherData } from '../../models/retorikTypes'
import { ContainerParent, Mode, Theme } from '../../models/enums'
import getThemeColors, { deepMerge } from '../../utils/getThemeColors'
import { getRetorikGDPR } from '../../utils/getGDPR'
import getRetorikConfigs from '../../utils/getConfigs'

import RetorikContainer from './RetorikContainer'
import Loader from '../Loader/Loader'
import getBaseAddress from '../../utils/getBaseAddress'
import { processLanguageData } from '../../utils/fetchAvailableLanguages'

interface RetorikConfigHandlerProps extends RetorikMainComponentProps {
  parent: number
}

const RetorikConfigHandler = ({
  config,
  viewsConfig,
  veilleConfig,
  mapConfig,
  chatbotData,
  chatbotDataWidget,
  agentData,
  ponyfillFactoryCredentials,
  customVoice,
  colors,
  skipLoader,
  defaultMode,
  GDPR,
  customScripts,
  customCSS,
  addressData,
  externalEventHandler,
  externalComponents,
  userData,
  width,
  height,
  menuConfig,
  environmentPreset,
  parent,
  enableVirtualKeyboard
}: RetorikConfigHandlerProps) => {
  const dispatch = useDispatch()
  const baseAddress = useSelector((state: RootState) => state.localeReducer.baseAddress)

  const [fetchedConfig, setFetchedConfig] = useState<RetorikMainComponentConfiguration & RetorikLanguageData & RetorikOtherData>()
  const [mainConfig, setMainConfig] = useState<RetorikMainComponentConfiguration | undefined>(undefined)
  const [isConfigUpdated, setIsConfigUpdated] = useState<boolean>(false)

  const fetchConfigurationFromRetorikStudio = async (baseAddress: string): Promise<void> => {
    const fetchedConfig = await getRetorikConfigs(
      {
        config,
        viewsConfig,
        veilleConfig,
        mapConfig,
        chatbotData,
        chatbotDataWidget,
        agentData,
        ponyfillFactoryCredentials,
        customVoice,
        colors,
        skipLoader,
        defaultMode,
        GDPR,
        addressData,
        externalEventHandler,
        externalComponents,
        userData,
        width,
        height,
        menuConfig,
        enableVirtualKeyboard
      },
      baseAddress
    )
    console.log('Retorik Framework > configuration fetched from Retorik Studio')
    fetchedConfig && setFetchedConfig(fetchedConfig)
  }

  const checkGDPR = async (baseAddress: string): Promise<void> => {
    const data = await getRetorikGDPR({
      configurationData: GDPR,
      baseAddress: baseAddress
    })

    // Set GDPR data in UtilsContext
    data.data && dispatch(storeActions.utils.setGDPRWindowData(data.data))
    dispatch(storeActions.utils.setGDPRDisplay(data.displayGDPR))
    typeof data.userConsent === 'boolean' && dispatch(storeActions.utils.setGDPRUserConsent(data.userConsent))
  }

  /**
   * Process addressData to get the baseAddress and then :
   * - fetch configuration and save it
   * - get GDPR data and store them
   * - fetch available languages and store them
   * - set baseAddress in store
   */
  useEffect(() => {
    if (addressData && (addressData.baseURI || addressData.tenant)) {
      const baseAddress = getBaseAddress(addressData)
      dispatch(storeActions.locale.setBaseAddress(baseAddress))
    }
  }, [addressData])

  useEffect(() => {
    if (baseAddress) {
      fetchConfigurationFromRetorikStudio(baseAddress)
      checkGDPR(baseAddress)
    }
  }, [baseAddress])

  useEffect(() => {
    if (environmentPreset) {
      dispatch(storeActions.retorik.setEnvironmentPreset(environmentPreset))
    }
  }, [environmentPreset])

  useEffect(() => {
    if (fetchedConfig) {
      const {
        skipLoader: _skipLoader,
        defaultMode: _defaultMode,
        config: _config,
        viewsConfig: _viewsConfig,
        veilleConfig: _veilleConfig,
        mapConfig: _mapConfig,
        chatbotData: _chatbotData,
        chatbotDataWidget: _chatbotDataWidget,
        agentData: _agentData,
        addressData: _addressData,
        ponyfillFactoryCredentials: _ponyfillFactoryCredentials,
        customVoice: _customVoice,
        colors: _colors,
        defaultCulture,
        supportedCultures,
        customScripts: _customScripts,
        customCSS: _customCSS,
        features
      } = fetchedConfig

      // Language
      if (defaultCulture && supportedCultures) {
        const { processedDefaultLanguage, processedSupported } = processLanguageData(defaultCulture, supportedCultures, true)
        processedDefaultLanguage && processedSupported && dispatch(storeActions.locale.setLanguageData({ defaultLocale: processedDefaultLanguage, supported: processedSupported }))
      } else {
        dispatch(storeActions.locale.fetchSupportedLanguages(baseAddress))
      }

      // Colors
      if (colors?.theme || _colors?.theme) {
        const tempTheme = colors?.theme || _colors?.theme
        if (tempTheme === Theme.auto && window.matchMedia) {
          // Use window.matchMedia to detect navigator's theme and set final theme instead of "auto"
          const theme = window.matchMedia('(prefers-color-scheme:light)')?.matches ? Theme.light : Theme.dark
          dispatch(storeActions.view.setTheme(theme))
          colors && (colors.theme = theme)
          _colors && (_colors.theme = theme)
        } else {
          dispatch(storeActions.view.setTheme(tempTheme === Theme.light ? Theme.light : Theme.dark))
        }
      } else if (window.matchMedia) {
        const tempTheme = window.matchMedia('(prefers-color-scheme:light)')?.matches ? Theme.light : Theme.dark
        dispatch(storeActions.view.setTheme(tempTheme))
        _colors && (_colors.theme = tempTheme)
      }

      const mergedConfig: Configuration = config !== undefined ? deepMerge(config, _config) : _config

      const mergedMainConfig: RetorikMainComponentConfiguration = {
        config: mergedConfig,
        viewsConfig: viewsConfig !== undefined ? deepMerge(viewsConfig, _viewsConfig) : _viewsConfig,
        veilleConfig: veilleConfig !== undefined ? deepMerge(veilleConfig, _veilleConfig) : _veilleConfig,
        mapConfig: mapConfig !== undefined ? deepMerge(mapConfig, _mapConfig) : _mapConfig,
        chatbotData: chatbotData !== undefined ? { ..._chatbotData, ...chatbotData } : _chatbotData,
        chatbotDataWidget:
          chatbotDataWidget !== undefined
            ? {
                ..._chatbotDataWidget,
                ...chatbotDataWidget
              }
            : _chatbotDataWidget,
        agentData: agentData !== undefined ? (typeof agentData === 'string' ? agentData : deepMerge(agentData, _agentData)) : _agentData,
        ponyfillFactoryCredentials: ponyfillFactoryCredentials !== undefined ? ponyfillFactoryCredentials : _ponyfillFactoryCredentials,
        customVoice: customVoice !== undefined ? { ..._customVoice, ...customVoice } : _customVoice,
        colors: colors !== undefined ? deepMerge(colors, getThemeColors(_colors)) : getThemeColors(_colors),
        skipLoader: skipLoader !== undefined ? skipLoader : _skipLoader,
        defaultMode: defaultMode !== undefined ? defaultMode : mergedConfig.disableSpeechMode ? Mode.text : mergedConfig.disableTextMode ? Mode.vocal : _defaultMode,
        // Data not retrieved from Retorik Studio
        addressData: _addressData,
        userData: userData,
        externalEventHandler: externalEventHandler,
        externalComponents: externalComponents,
        width: width,
        height: height,
        enableVirtualKeyboard: enableVirtualKeyboard
      }

      // Prevent menus from being copied again after each modification of configuration
      if (mergedMainConfig.config) {
        mergedMainConfig.config.baseMenu = config?.baseMenu || menuConfig?.baseMenu
        mergedMainConfig.config.customMenu = config?.customMenu || menuConfig?.customMenu
        mergedMainConfig.config.subMenu = config?.subMenu || menuConfig?.subMenu
        mergedMainConfig.config.menu = config?.menu || menuConfig?.menu || _config?.menu
      }

      dispatch(storeActions.retorik.setCustomScripts(customScripts || _customScripts || []))
      dispatch(storeActions.retorik.setCustomCSS(customCSS || _customCSS || []))
      dispatch(storeActions.retorik.setEnabledFeatures(features || []))
      // Save this config and use it in the application
      setMainConfig(mergedMainConfig)
      setIsConfigUpdated(true)
    }
  }, [
    config,
    viewsConfig,
    veilleConfig,
    chatbotData,
    chatbotDataWidget,
    agentData,
    ponyfillFactoryCredentials,
    customVoice,
    colors,
    skipLoader,
    defaultMode,
    GDPR,
    customScripts,
    addressData,
    externalEventHandler,
    externalComponents,
    userData,
    width,
    height,
    menuConfig,
    fetchedConfig
  ])

  return mainConfig ? (
    <RetorikContainer parent={parent} {...mainConfig} />
  ) : (
    <Loader
      className={isConfigUpdated ? 'rf-animate-loaderFadeOut' : ''}
      parent={parent}
      width={width}
      height={height}
      fullSize={!!(parent !== ContainerParent.widget && config?.fullSize !== false)}
    />
  )
}

export default RetorikConfigHandler
